import React, { FC, memo, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  Divider,
  Typography
} from '@material-ui/core';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import { TextRow } from 'src/views/product/component/TextRow';
import { AlternateRowContainer } from 'src/views/product/component/AlternateRowContainer';
import { LinkComponent } from 'src/components';
import { copyToClipboard, formatCurrency } from 'src/utils';
import { priceColumn } from 'src/utils/fast-moving-products';

const useStyles = makeStyles({
  accordion: {
    padding: 5,
    marginTop: 15
  },
  card: {
    marginTop: '1rem',
    position: 'relative'
  },
  rowHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem'
  },
  productBox: {
    display: 'flex',
    alignItems: 'center',
    padding: 8,
    marginTop: 10
  },
  copyIcon: {
    cursor: 'pointer',
    marginRight: 10
  },
  calendarIcon: {
    cursor: 'pointer',
    marginRight: 5
  },
  cellRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.5rem 0.75rem',
    borderBottom: '1px solid #eee'
  },
  cellLabel: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: '0.95rem',
    fontWeight: 500,
    lineHeight: 1.5,
    letterSpacing: '0.00938em',
    color: '#555'
  },
  cellValue: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0.00938em',
    color: '#111'
  }
});

interface Props {
  filteredShowColumns: any;
  calculatedTotals: any;
  customFastMovingData: any;
  onHandleViewDate: (product_id: string) => void;
}

const Component: FC<Props> = ({
  filteredShowColumns,
  calculatedTotals,
  customFastMovingData,
  onHandleViewDate
}) => {
  const classes = useStyles();

  const onHandleCopyProductName = useCallback((productName?: string) => {
    copyToClipboard(productName || '');
  }, []);

  const handleFastMovingCellValue = (
    header: string,
    data: any
  ): string | number => {
    const value = data[header];
    if (header.toLowerCase() === 'sku' && !value) return 'N/A';
    if (priceColumn.includes(header)) return formatCurrency(value ?? 0);
    if (header === 'margin_percentage') return `${value ?? 0}%`;
    return value ?? 0;
  };

  return (
    <>
      {filteredShowColumns && (
        <Accordion className={classes.accordion}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="summary-content"
            id="summary-header"
          >
            <Typography variant="h4">Total Summary Data</Typography>
          </AccordionSummary>
          <AccordionDetails style={{ flexDirection: 'column', padding: 0 }}>
            {filteredShowColumns
              .filter((header: any) => header !== 'product_name')
              .map((header: any, index: number) => (
                <AlternateRowContainer key={index} index={index}>
                  <TextRow
                    padding="0.5rem"
                    label={header.replace(/_/g, ' ')}
                    component={
                      <Typography variant="h5">
                        {calculatedTotals[header] || 0}
                      </Typography>
                    }
                  />
                </AlternateRowContainer>
              ))}
          </AccordionDetails>
        </Accordion>
      )}
      <Card elevation={1} className={classes.card}>
        {customFastMovingData?.map((data: any, index: number) => (
          <AlternateRowContainer index={index} key={data?.product_id}>
            <div className={classes.rowHeader}>
              <Box className={classes.productBox}>
                <FileCopyIcon
                  className={classes.copyIcon}
                  onClick={() => onHandleCopyProductName(data['product_name'])}
                />
                <LinkComponent
                  openInNewTab
                  href={`/app/products/${data?.product_id}`}
                  title={data['product_name'] ?? ''}
                  font="Roboto, Helvetica, Arial, sans-serif"
                />
              </Box>
              <CalendarTodayIcon
                className={classes.calendarIcon}
                onClick={() => onHandleViewDate(data?.product_id)}
              />
            </div>
            <Divider />

            {filteredShowColumns?.map((header: any) =>
              header !== 'product_name' ? (
                <div key={header} className={classes.cellRow}>
                  <div className={classes.cellLabel}>
                    {header?.replace(/_/g, ' ')}
                  </div>
                  <div className={classes.cellValue}>
                    {handleFastMovingCellValue(header, data)}
                  </div>
                </div>
              ) : null
            )}
          </AlternateRowContainer>
        ))}
      </Card>
    </>
  );
};

export const MobileDataTable = memo(Component);
